import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { methodShipment } from '../../../../Components/helper';
import { getBomSupplier } from '../../../../services/bom-config.service';
import { getActiveIncotermsDetails } from '../../../SystemMasters/Store/SystemMasters.action';
import type { CostingDispatch, CostingRecord, NamedRef } from './store/Costing.type';
import { getProductCostingSampleRefs } from '../../../../services/product-costing.service';

/* Dropdown option sources for the editable Costing TODO columns.

   Suppliers and factories are NOT tenant-wide here. A costing may only use the suppliers assigned to
   its own development order, exactly as the detail page does via `fetchSupplier(devOrderId)`
   (ProductInfoTable.js:550). `GET /api/order/supplier` returns those suppliers with their factories
   attached, so one request per development order on the page supplies both dropdowns and keeps the
   options scoped to the product.

   Incoterms and costing stages are genuine page-level masters and come from redux. */

export interface SelectOption {
  label: string;
  value: string;
  /** The full record, so an edit can read the supplier's currency or the factory's MOQ. */
  raw?: unknown;
}

export interface CostingTodoOptions {
  /** Suppliers assigned to each development order on the page, keyed by development order id. */
  supplierOptionsByDevOrder: Record<string, SelectOption[]>;
  incotermOptions: SelectOption[];
  sampleRefOptionsByDevOrder: Record<string, SelectOption[]>;
  shipmentMethodOptions: SelectOption[];
  factoryOptionsBySupplier: Record<string, SelectOption[]>;
}

interface FactoryRecord {
  _id?: string;
  name?: string;
  factoryName?: string;
}

interface SupplierRecord {
  _id?: string;
  name?: string;
  supplierName?: string;
  /** An object on the dev-order endpoint, an array on the tenant-wide one. */
  companies?: { supplierName?: string; currency?: string } | Array<{ supplierName?: string; currency?: string }>;
  factories?: FactoryRecord[];
}

interface OptionsRootState {
  SystemMastersDetails: {
    activeIncotermsList?: NamedRef[];
  };
}

/** Reads a supplier's display name out of either `companies` shape. */
function supplierLabel(record: SupplierRecord | undefined): string | undefined {
  const companies = record?.companies;
  const fromCompanies = Array.isArray(companies) ? companies[0]?.supplierName : companies?.supplierName;
  return fromCompanies ?? record?.supplierName ?? record?.name;
}

/** Maps any `{ _id, name }`-ish record to a select option, keeping the source for cascade reads. */
function toOption(record: SupplierRecord | FactoryRecord | NamedRef | undefined, label?: string): SelectOption | null {
  const value = record?._id;
  if (!value) return null;
  const resolvedLabel = label ?? supplierLabel(record as SupplierRecord) ?? (record as FactoryRecord)?.factoryName ?? record?.name ?? '';
  return { label: String(resolvedLabel), value, raw: record };
}

/**
 * Narrows a row's factory options to the ones it may actually use.
 *
 * A factory has to satisfy both constraints the detail page applies
 * (`ProductInfoTable.js:435-441`): it belongs to the costing's currently selected supplier, and it
 * was chosen for this product when the product was created (`supplierFactories`). A product with an
 * empty `supplierFactories` offers no factories at all — that is the intended outcome, not a
 * missing-data fallback, so it deliberately does not fall back to the supplier's full list.
 * @param row the grid row
 * @param factoryOptionsBySupplier every supplier's factories, indexed by supplier id
 * @returns the options this row's Factory dropdown may show
 */
export function resolveFactoryOptions(
  row: { cost?: { productInfo?: { supplierId?: unknown }; supplierFactories?: string[] } } | undefined,
  factoryOptionsBySupplier: Record<string, SelectOption[]>,
): SelectOption[] {
  const supplierId = row?.cost?.productInfo?.supplierId;
  const supplierKey = supplierId && typeof supplierId === 'object' ? String((supplierId as { _id?: unknown })._id ?? '') : String(supplierId ?? '');
  const supplierFactories = factoryOptionsBySupplier[supplierKey] ?? [];
  const allowed = row?.cost?.supplierFactories;
  if (!Array.isArray(allowed)) return supplierFactories;
  const allowedIds = new Set(allowed.map((id) => String(id)));
  return supplierFactories.filter((option) => allowedIds.has(String(option.value)));
}

/**
 * Normalises the supplier response to a flat list.
 * The dev-order endpoint resolves to an array; the tenant-wide one wraps it in `{ suppliers }`.
 * Reading either as an array leaves the dropdown silently empty.
 * @param response whatever the supplier request resolved to
 * @returns the supplier records, or an empty list
 */
export function toSupplierRecords(response: unknown): SupplierRecord[] {
  if (Array.isArray(response)) return response as SupplierRecord[];
  const envelope = response as { data?: unknown; suppliers?: unknown } | undefined;
  if (Array.isArray(envelope?.suppliers)) return envelope.suppliers as SupplierRecord[];
  if (Array.isArray(envelope?.data)) return envelope.data as SupplierRecord[];
  const nested = (envelope?.data as { suppliers?: unknown } | undefined)?.suppliers;
  return Array.isArray(nested) ? (nested as SupplierRecord[]) : [];
}

/**
 * Loads the option lists the editable Costing TODO columns need.
 * Supplier and factory options are fetched per development order present on the loaded page, so a
 * row only ever offers the suppliers its own product is assigned.
 * @param costingData the currently loaded costings, used to scope the supplier lookup
 * @returns the page-level option lists plus the dev-order and supplier indexed maps
 */
export function useCostingTodoOptions(costingData: CostingRecord[]): CostingTodoOptions {
  const dispatch = useDispatch<CostingDispatch>();
  const {
    SystemMastersDetails: { activeIncotermsList },
  } = useSelector((state: OptionsRootState) => state);

  const [suppliersByDevOrder, setSuppliersByDevOrder] = useState<Record<string, SupplierRecord[]>>({});
  const [sampleRefsByDevOrder, setSampleRefsByDevOrder] = useState<Record<string, SelectOption[]>>({});

  const devOrderIds = useMemo(() => {
    const ids = new Set<string>();
    (costingData ?? []).forEach((costing) => {
      const devOrderId = costing?.devOrderId;
      if (devOrderId) ids.add(String(devOrderId));
    });
    return Array.from(ids).sort();
  }, [costingData]);

  const devOrderKey = devOrderIds.join(',');

  useEffect(() => {
    if (devOrderIds.length === 0) return undefined;
    let cancelled = false;

    const loadSuppliers = async () => {
      /* allSettled so one inaccessible development order cannot empty every other row's dropdown. */
      const responses = await Promise.allSettled(devOrderIds.map((devOrderId) => getBomSupplier(devOrderId)));
      if (cancelled) return;
      setSuppliersByDevOrder((previous) => {
        const next = { ...previous };
        devOrderIds.forEach((devOrderId, index) => {
          const response = responses[index];
          next[devOrderId] = response?.status === 'fulfilled' ? toSupplierRecords(response.value) : [];
        });
        return next;
      });
    };

    loadSuppliers();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devOrderKey]);

  /* Sample Refs belong to one product, so like suppliers they are indexed by development order
     rather than held as a page-level list. One batch call covers every product on the page. */
  useEffect(() => {
    if (devOrderIds.length === 0) return undefined;
    let cancelled = false;

    const loadSampleRefs = async () => {
      const responses = await Promise.allSettled(devOrderIds.map((devOrderId) => getProductCostingSampleRefs({ devOrderId })));
      if (cancelled) return;
      setSampleRefsByDevOrder((previous) => {
        const next = { ...previous };
        devOrderIds.forEach((devOrderId, index) => {
          const response = responses[index];
          /* apiClient unwraps to the envelope body, so the array is `.data`, not `.data.data`. */
          const rows = response?.status === 'fulfilled' ? ((response.value as { code?: number; data?: Array<{ _id?: string; sampleNumber?: string; label?: string }> })?.data ?? []) : [];
          next[devOrderId] = rows.map((row) => ({ value: String(row._id ?? ''), label: row.label || row.sampleNumber || '' })).filter((option) => option.value);
        });
        return next;
      });
    };

    loadSampleRefs();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devOrderKey]);

  /* Incoterms come from redux rather than the thunk's return value: the thunk resolves to the
     `{ code, data }` envelope, and the detail page reads the same `activeIncotermsList` slice. */
  useEffect(() => {
    dispatch(getActiveIncotermsDetails({ isActive: true }) as never);
  }, [dispatch]);

  const supplierOptionsByDevOrder = useMemo(() => {
    const map: Record<string, SelectOption[]> = {};
    Object.entries(suppliersByDevOrder).forEach(([devOrderId, records]) => {
      map[devOrderId] = records.map((record) => toOption(record)).filter(Boolean) as SelectOption[];
    });
    return map;
  }, [suppliersByDevOrder]);

  /* Factories ride along on the supplier response, so a supplier's factories are always the ones
     that supplier actually has on this development order. */
  const factoryOptionsBySupplier = useMemo(() => {
    const map: Record<string, SelectOption[]> = {};
    Object.values(suppliersByDevOrder).forEach((records) => {
      records.forEach((record) => {
        const supplierId = record?._id ? String(record._id) : '';
        if (!supplierId) return;
        const options = (record.factories ?? []).map((factory) => toOption(factory)).filter(Boolean) as SelectOption[];
        const seen = new Set((map[supplierId] ?? []).map((option) => option.value));
        map[supplierId] = [...(map[supplierId] ?? []), ...options.filter((option) => !seen.has(option.value))];
      });
    });
    return map;
  }, [suppliersByDevOrder]);

  const incotermOptions = useMemo(() => (activeIncotermsList ?? []).map((record) => toOption(record)).filter(Boolean) as SelectOption[], [activeIncotermsList]);

  const sampleRefOptionsByDevOrder = useMemo(() => sampleRefsByDevOrder, [sampleRefsByDevOrder]);

  const shipmentMethodOptions = useMemo(
    () => (methodShipment as Array<{ value: string; label: string }>).map((method) => ({ label: method.label, value: method.value })),
    [],
  );

  /* Memoised as a whole, not just field by field. The column builder keys on this object's
     identity: a fresh literal every render would rebuild every column definition every render,
     which AG Grid treats as a column change and answers by resetting column order and horizontal
     scroll. Stable while the options are, and a new object the moment any of them arrives. */
  return useMemo(
    () => ({ supplierOptionsByDevOrder, incotermOptions, sampleRefOptionsByDevOrder, shipmentMethodOptions, factoryOptionsBySupplier }),
    [supplierOptionsByDevOrder, incotermOptions, sampleRefOptionsByDevOrder, shipmentMethodOptions, factoryOptionsBySupplier],
  );
}
